import * as htmlToImage from 'html-to-image'

/**
 * Rasterize a tldraw custom shape's live DOM into an SVG `<image>` element,
 * suitable as a `ShapeUtil.toSvg()` return value.
 *
 * Why this exists: our `HTMLContainer`-based shapes (DocPreview / ConnectorCard
 * / CommentPin) don't have a hand-written SVG representation. Without a
 * `toSvg` override, `editor.toImage()` renders them as blanks. With this
 * helper, each shape util can hand off to its already-mounted DOM and let
 * html-to-image produce the pixels.
 *
 * The shape's inner `.tl-html-container` is sized in *local* (shape-space)
 * coordinates — its parent `.tl-shape` wrapper carries the camera transform,
 * but the container itself is at natural size. Rasterizing the container
 * directly therefore yields camera-independent pixels, which is exactly
 * what auto-preview wants (no `setCamera` dance needed).
 *
 * Returns `null` if the shape isn't mounted (e.g. culled off-screen). The
 * caller — `editor.toImage` — handles a `null` by falling back to whatever
 * default representation tldraw has for the shape (typically nothing). A
 * culled shape wouldn't be on-camera anyway, so a missing pixel-perfect
 * thumbnail of it isn't a quality regression.
 */
export async function shapeNodeToSvgImage(
  shapeId: string,
  width: number,
  height: number,
): Promise<React.ReactElement | null> {
  const wrapper = document.querySelector<HTMLElement>(`[data-shape-id="${shapeId}"]`)
  if (!wrapper) return null
  const inner = wrapper.querySelector<HTMLElement>('.tl-html-container') ?? wrapper

  let dataUrl: string
  try {
    dataUrl = await htmlToImage.toPng(inner, {
      pixelRatio: 1,
      backgroundColor: '#ffffff',
      // Pass explicit dimensions: html-to-image otherwise sizes the output
      // canvas from `node.clientWidth` / `node.clientHeight`, which are
      // zero if any ancestor is `display: none` (tldraw's cull state). The
      // caller in captureBoardPreview already force-mounts culled wrappers,
      // but this is a belt-and-suspenders against any reactor race.
      width,
      height,
    })
  } catch {
    return null
  }

  return (
    <image
      href={dataUrl}
      width={width}
      height={height}
      preserveAspectRatio="xMidYMid meet"
    />
  )
}
